// src/pages/SesionesPage.js
//
// Antes "MasterTalksPage.js" — renombrado por decisión de producto:
// no se usa el nombre "Master Talks" (riesgo comercial/marca).
// Ahora se llama "Sesiones" y cubre dos tipos de contenido en vivo:
//   - Sesión Online:  clase en vivo vía Google Meet (backend genera
//                      el link automáticamente al crear la sesión).
//   - Sesión Grabada:  video pregrabado disponible bajo demanda.
//
// Ya no depende de masterTalkStore (datos en memoria) — consume
// sesionOnlineService y sesionGrabadaService reales.

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Chip,
  TextField,
  MenuItem,
  InputAdornment,
  Skeleton,
  Avatar,
} from '@mui/material';
import {
  Search,
  VideoCall,
  PlayCircleOutline,
  Schedule,
  FiberManualRecord,
  School,
  RecordVoiceOver,
} from '@mui/icons-material';
import { sesionOnlineService, sesionGrabadaService } from '../services/api';

const TIPO_LABELS = {
  ClaseEnVivo: 'Clase en vivo',
  SesionPractica: 'Sesión práctica',
  PresentacionProyecto: 'Presentación de proyecto',
  SesionOrientacion: 'Sesión de orientación',
};

function SesionesPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0); // 0 = Próximas, 1 = En vivo, 2 = Grabaciones
  const [search, setSearch] = useState('');

  const [proximas, setProximas] = useState([]);
  const [enVivo, setEnVivo] = useState([]);
  const [grabadas, setGrabadas] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [prog, live, rec] = await Promise.all([
      sesionOnlineService.getAll({ status: 'Programada', limit: 50 }),
      sesionOnlineService.getAll({ status: 'EnVivo', limit: 50 }),
      sesionGrabadaService.getAll({ limit: 50 }),
    ]);
    setProximas(prog.data);
    setEnVivo(live.data);
    setGrabadas(rec.data);
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const filterBySearch = (list, titleKey = 'title') =>
    list.filter((s) => s[titleKey]?.toLowerCase().includes(search.toLowerCase()));

  const currentList =
    tab === 0 ? filterBySearch(proximas) :
    tab === 1 ? filterBySearch(enVivo) :
    filterBySearch(grabadas);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <School color="primary" />
        <Typography variant="h4" fontWeight={700}>Sesiones</Typography>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        Sesiones en vivo, clases magistrales y videos grabados
      </Typography>

      {/* Stat cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}><Schedule /></Avatar>
              <Box>
                <Typography variant="body2" color="text.secondary">Programadas</Typography>
                <Typography variant="h5" fontWeight={700}>{proximas.length}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'error.main' }}><FiberManualRecord /></Avatar>
              <Box>
                <Typography variant="body2" color="text.secondary">En vivo</Typography>
                <Typography variant="h5" fontWeight={700}>{enVivo.length}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'success.main' }}><PlayCircleOutline /></Avatar>
              <Box>
                <Typography variant="body2" color="text.secondary">Grabaciones</Typography>
                <Typography variant="h5" fontWeight={700}>{grabadas.length}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<Schedule fontSize="small" />} iconPosition="start" label="Próximas" />
        <Tab icon={<FiberManualRecord fontSize="small" color="error" />} iconPosition="start" label="En vivo" />
        <Tab icon={<PlayCircleOutline fontSize="small" />} iconPosition="start" label="Grabaciones" />
      </Tabs>

      {/* Búsqueda */}
      <TextField
        fullWidth
        placeholder="Buscar sesión..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
        }}
      />

      {/* Listado */}
      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={200} />
            </Grid>
          ))}
        </Grid>
      ) : currentList.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <VideoCall sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>No hay sesiones aquí todavía</Typography>
          <Typography color="text.secondary">
            {tab === 0 && 'No hay sesiones online programadas.'}
            {tab === 1 && 'No hay sesiones en vivo en este momento.'}
            {tab === 2 && 'No hay grabaciones disponibles.'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {currentList.map((s) => (
            <Grid item xs={12} sm={6} md={4} key={s.id}>
              {tab === 2 ? (
                <RecordedCard sesion={s} />
              ) : (
                <OnlineCard sesion={s} onJoin={() => navigate(`/sesiones/${s.id}/vivo`)} />
              )}
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}

function OnlineCard({ sesion, onJoin }) {
  const isLive = sesion.status === 'EnVivo';
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          height: 100,
          background: isLive
            ? 'linear-gradient(135deg, #e94560, #ff6b6b)'
            : 'linear-gradient(135deg, #3b82f6, #06b6d4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {isLive ? <FiberManualRecord sx={{ fontSize: 40, color: 'white' }} /> : <VideoCall sx={{ fontSize: 40, color: 'white' }} />}
      </Box>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Chip
          label={TIPO_LABELS[sesion.type] || sesion.type}
          size="small"
          color={isLive ? 'error' : 'primary'}
          sx={{ alignSelf: 'flex-start', mb: 1 }}
        />
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>{sesion.title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {sesion.courseTitle}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <RecordVoiceOver fontSize="small" color="disabled" />
          <Typography variant="body2" color="text.secondary">{sesion.instructorName}</Typography>
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ mb: 2 }}>
          {new Date(sesion.startAt).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })}
          {' · '}{sesion.durationMinutes} min
        </Typography>
        <Box sx={{ mt: 'auto' }}>
          <Button
            fullWidth
            variant="contained"
            color={isLive ? 'error' : 'primary'}
            startIcon={isLive ? <FiberManualRecord /> : <VideoCall />}
            onClick={onJoin}
          >
            {isLive ? 'Unirse ahora' : 'Ver detalle'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

function RecordedCard({ sesion }) {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        component={sesion.thumbnail ? 'img' : 'div'}
        src={sesion.thumbnail}
        sx={{
          height: 140, objectFit: 'cover',
          background: sesion.thumbnail ? undefined : 'linear-gradient(135deg, #10b981, #059669)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {!sesion.thumbnail && <PlayCircleOutline sx={{ fontSize: 40, color: 'white' }} />}
      </Box>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom noWrap>{sesion.title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{sesion.courseTitle}</Typography>
        <Typography variant="caption" color="text.disabled" sx={{ mb: 2 }}>{sesion.instructorName}</Typography>
        <Box sx={{ mt: 'auto' }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<PlayCircleOutline />}
            component="a"
            href={sesion.videoUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Ver grabación
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default SesionesPage;
